import { useCallback } from 'react';

import type { Dispatch, SetStateAction } from 'react';
import type { FilterState, SortState } from '../../components';
import type { PaginationState } from './types';

export type AutoResetTrigger = 'filter' | 'sort' | 'pageSize';

export type AutoResetOptions = {
  /**
   * 페이지 인덱스 자동 초기화 설정
   * true: 모든 변화에 페이지=1 초기화
   * 배열: 지정된 변화에만 페이지=1 초기화
   */
  autoResetPageIndex?: AutoResetTriggerOption;
  /**
   * 정렬 상태 자동 초기화 설정
   * true: 모든 변화에 정렬을 initialState.sort로 초기화
   * 배열: 지정된 변화에만 정렬 초기화
   */
  autoResetSort?: AutoResetTriggerOption;
};

export type AutoResetTriggerOption = boolean | Partial<Record<AutoResetTrigger, boolean>>;

interface InitialState {
  filter?: FilterState[];
  page?: PaginationState;
  sort?: SortState[];
}

export interface UseAutoResetHandlersParams {
  context: {
    setPageState: Dispatch<SetStateAction<PaginationState>>;
    setSortState: Dispatch<SetStateAction<SortState<Record<string, unknown>>[]>>;
    initialState?: InitialState;
  };
  autoReset?: AutoResetOptions;
}

// 자동 초기화 기본값 - 정책 스펙에 따른 권장 설정
const DEFAULT_AUTO_RESET_PAGE_INDEX: AutoResetTriggerOption = true; // 모든 변화에 페이지=1 초기화
const DEFAULT_AUTO_RESET_SORT: AutoResetTriggerOption = { filter: true }; // 필터 변경시에만 정렬 초기화

const useAutoResetHandlers = (params: UseAutoResetHandlersParams) => {
  const { autoReset, context } = params ?? {};
  const { setPageState, setSortState, initialState } = context;
  const { sort: initialSortState = [] } = initialState ?? {};

  const { autoResetPageIndex, autoResetSort } = autoReset ?? {};

  // 자동 초기화 헬퍼 함수들
  const shouldAutoReset = useCallback(
    (
      option: AutoResetTriggerOption | undefined = {},
      defaultOption: AutoResetTriggerOption,
      trigger: AutoResetTrigger,
    ): boolean => {
      if (typeof option === 'boolean') return option;
      if (option[trigger] !== undefined) return option[trigger];
      if (typeof defaultOption === 'boolean') return defaultOption;
      return !!defaultOption[trigger];
    },
    [],
  );

  /**
   * autoResetPageIndex 옵션에 `trigger`에 해당하는 값이 포함된 경우 페이지 인덱스를 1로 초기화합니다.
   */
  const resetPageIndexIfNeeded = useCallback(
    (trigger: AutoResetTrigger) => {
      if (shouldAutoReset(autoResetPageIndex, DEFAULT_AUTO_RESET_PAGE_INDEX, trigger)) {
        setPageState(prev => ({ ...prev, pageIndex: 1 }));
      }
    },
    [shouldAutoReset, autoResetPageIndex, setPageState],
  );

  /**
   * autoResetSort 옵션에 `trigger`에 해당하는 값이 포함된 경우 정렬 상태를 초기화합니다.
   */
  const resetSortIfNeeded = useCallback(
    (trigger: AutoResetTrigger) => {
      if (shouldAutoReset(autoResetSort, DEFAULT_AUTO_RESET_SORT, trigger)) {
        setSortState(initialSortState);
      }
    },
    [shouldAutoReset, autoResetSort, setSortState, initialSortState],
  );

  return {
    resetPageIndexIfNeeded,
    resetSortIfNeeded,
  };
};

export default useAutoResetHandlers;
